/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * SIM service
 */

namespace Application.Services {
    'use strict';

    import ISimPlan = Models.ISimPlan;
    import SIM = Models.SIM;
    import ISIM = Models.ISIM;
    import INetworkNode = Models.INetworkNode;
    import IHalList = Models.IHalList;
    import HalConverter = Models.HalConverter;
    import IHalElement = Models.IHalElement;
    import IPaginatedList = Models.IPaginatedList;
    import ISIMDataUsages = Models.ISIMDataUsages;

    /**
     * NetworkNodeService
     */
    export class SIMService {
        public static $name: string = "simService";
        public static $inject: string[] = ['backendService',
                                           'simPlanService',
                                           'networkNodeModemService',
                                           'networkNodeService',
                                           '$q',
                                           ];

        private backendService: BackendService;
        private backendBaseUrl: string = window.backendUrl;
        private servicePath: string = '';
        private backendUrl: string = this.backendBaseUrl + this.servicePath + 'sims';
        private dataUsageUrl: string = this.backendBaseUrl  + 'simDataUsages/';
        private dataUsageSearchUrlPaged: string = this.dataUsageUrl + 'search/findBySimIdOrderByDateDesc?sim_id=';
        private dataRemainingChartUrl: string = this.dataUsageUrl + 'graph/';
        private dataUsageChartUrl: string = this.dataUsageUrl + 'usageGraph/';
        private networkNodeUrl: string = this.backendBaseUrl + this.servicePath + 'networkNodes';
        private dashboardDataUrl: string = this.backendUrl + '/dashboardData';
        private dashboardMobileDevicesUrl: string = this.backendUrl + '/dashboardMobileDevices';

        private simPlanService: Services.SimPlanService;
        private networkNodeModemService: Services.NetworkNodeModemService;
        private networkNodeService: Services.NetworkNodeService;
        private qService: ng.IQService;

        constructor(backendService: BackendService,
                    simPlanService: Services.SimPlanService,
                    networkNodeModemService: Services.NetworkNodeModemService,
                    networkNodeService: Services.NetworkNodeService,
                    $q: ng.IQService) {
            this.backendService = backendService;
            this.simPlanService = simPlanService;
            this.networkNodeModemService = networkNodeModemService;
            this.networkNodeService = networkNodeService;
            this.qService = $q;
        }

        // tODO: replace this call with a unique backend call to retrieve all data
        public getAllSIMs(): ng.IPromise<Models.IPaginatedList<SIM>> {
            return this.getAllPaginatedSimsAndDetails(0, 9999999);
        }

        // sims
        public getAllPaginatedSimsAndDetails(page?: number,
                                             size?: number,
                                             sort?: string,
                                             simPlans?: ISimPlan[]): ng.IPromise<Models.IPaginatedList<SIM>> {
            let promises: ng.IPromise<Object>[];
            if (simPlans === undefined || simPlans.length <= 0)
                promises = [
                    this.getAllPaginatedSims(page, size, sort),
                    this.networkNodeModemService.getAllPaginatedNetworkNodeModems(undefined, 999999999, undefined),
                    this.networkNodeService.getAllNetworkNodesPromise(),
                    this.getAllSimPlans(),
                ];
            else
                promises = [
                    this.getAllPaginatedSims(page, size, sort),
                    this.networkNodeModemService.getAllPaginatedNetworkNodeModems(undefined, 999999999, undefined),
                    this.networkNodeService.getAllNetworkNodesPromise(),
                ];

            return this.qService
                .all(promises)
                .then((res: Object[]) => {
                    let sims: Models.IPaginatedList<SIM> = res[0] as Models.IPaginatedList<SIM>;
                    let networkNodeModems: Models.INetworkNodeModemAdapter[] =
                        (res[1] as Models.IPaginatedList<Models.INetworkNodeModemAdapter>).elements;
                    let networkNodes: Models.INetworkNode[] = res[2] as Models.INetworkNode[];
                    if (simPlans === undefined || simPlans.length <= 0)
                        simPlans = res[3] as ISimPlan[];
                    sims.elements.forEach((sim: ISIM) => {
                        for (let i: number = 0; i < simPlans.length; i++) {
                            if (simPlans[i].href === sim.simPlan.href) {
                                sim.simPlan = simPlans[i];
                                break;
                            }
                        }
                        for (let i: number = 0; i < networkNodeModems.length; i++) {
                            if (sim.networkNodeModem &&
                                networkNodeModems[i].href === sim.networkNodeModem.href) {
                                sim.networkNodeModem = networkNodeModems[i] as Models.NetworkNodeModemAdapter;
                                break;
                            }
                        }
                        for (let i: number = 0; i < networkNodes.length; i++) {
                            if (sim.networkNodeModem &&
                                Common.Utils.idFromHref(networkNodes[i].href) === sim.networkNodeModem.networkNode) {
                                sim.networkNodeModem.networkNode = networkNodes[i];
                                break;
                            }
                        }
                    });
                    return this.qService.all(
                        sims.elements.map(
                            (sim: SIM) => {
                                return this.getRemainingData(Common.Utils.idFromHref(sim.href));
                            }
                        ))
                        .then((remainingData: number[]) => {
                            for (let i: number = 0; i < remainingData.length; i++)
                                (sims.elements[i] as ISIM).remainingData = remainingData[i];
                            return sims;
                        });
                });
        }

        public getRemainingData(simId: number): ng.IPromise<number> {
            return this.backendService
                .get(this.backendUrl + '/' + simId + '/dataRemaining')
                .then((dataRemaining: ng.IHttpPromiseCallbackArg<number>) => {
                    return dataRemaining.data;
                });
        }

        // simUsage
        public getSIMAndPlanBySIMIdPromise(id: number): ng.IPromise<Models.SIM> {
            return this.qService.all([
                this.getSIMByIdPromise(id),
                this.getSIMPlanBySIMIdPromise(id),
                this.getRemainingData(id),
                this.networkNodeModemService.findModemBySimId(id),
            ])
            .then((res: Object[]) => {
                let sim: Models.ISIM = res[0] as Models.SIM;
                sim.simPlan = res[1] as Models.SimPlan;
                sim.remainingData = res[2] as number;
                sim.networkNodeModem = res[3] as Models.NetworkNodeModemAdapter;
                this.burnishSIM(sim);
                return sim;
            });
        }

        // sims (used to check whether a given ICCID already exists)
        public getSIMByIccid(iccid: string, callback: (result: SIM) => void,
                             error: (reason: ngCustom.IReason) => void): void {
            this.backendService
                .get(this.backendUrl + "/search/findByIccid?iccid=" + iccid)
                .then((data: IHalElement<SIM>) => {
                    let halElement: IHalElement<SIM> = data;
                    let converted: SIM = HalConverter.convertElement(halElement, SIM.Empty);
                    this.burnishSIM(converted);
                    callback(converted);
                })
                .catch((reason: ngCustom.IReason) => {
                    error(reason);
            });
        }

        public existsSIMByIccid(iccid: string): ng.IPromise<boolean> {
            return this.getSIMByIccidPromise(iccid, false)
                .then((sim: ISIM) => {
                    return (sim && sim.iccid === iccid);
                })
                .catch((sim: ISIM) => {
                    return false;
                });
        }

        public saveSIM(sim: ISIM): ng.IPromise<ISIM> {
            if (!sim.href || sim.href === undefined || sim.href === '') {
                return this.createSIM(sim);
            } else {
                return this.updateSIM(sim);
            }
        }

        public createSIM(sim: ISIM): ng.IPromise<ISIM> {
            return this.backendService
                .post(this.backendUrl, {
                    creationDate: sim.creationDate,
                    iccid: sim.iccid,
                    pin: sim.pin,
                    puk: sim.puk,
                    simPlan: sim.simPlan.href,
                    simPlanEndDate: sim.simPlanEndDate,
                    networkNodeModem: sim.networkNodeModem ? sim.networkNodeModem.href : undefined,
                    billingCycle: sim.billingCycle,
                    localization: sim.localization,
                })
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<ISIM>>) => {
                    let halElement: IHalElement<SIM> = result.data;
                    let converted: SIM = HalConverter.convertElement(halElement, Models.SIM.Empty);
                    return this.burnishSIM(converted);
                 });
        }

        public updateSIM(sim: ISIM): ng.IPromise<ISIM> {
            return this.backendService
                .put(sim.href, {
                    iccid: sim.iccid,
                    pin: sim.pin,
                    puk: sim.puk,
                    simPlan: sim.simPlan.href,
                    simPlanEndDate: sim.simPlanEndDate,
                    networkNodeModem: sim.networkNodeModem ? sim.networkNodeModem.href : undefined,
                    billingCycle: sim.billingCycle,
                    localization: sim.localization,
                })
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Models.INetworkNode>>) => {
                    let halElement: IHalElement<SIM> = result.data;
                    let converted: SIM = HalConverter.convertElement(halElement, SIM.Empty);
                    return this.burnishSIM(converted);
                 }
            );
        }

        public getTotalDataUsage(simId: number, dFrom: Date): ng.IPromise<number> {
            if (!simId || !dFrom)
                return undefined;
            let url: string;
            url = this.dataUsageUrl + simId + '/dataUsageFromDate/' + Common.Utils.dateToString(dFrom);
            return this.backendService
                .get(url)
                .then((totalDataUsage: ng.IHttpPromiseCallbackArg<number>) => {
                    return totalDataUsage.data;
                });
        }

        public getDataUsage(simId: number, page?: number, size?: number, sort?: string): ng.IPromise<IPaginatedList<ISIMDataUsages>> {
            let url: string = this.dataUsageSearchUrlPaged + simId;
            url = page ? url += '&page=' + page : url;
            url = size ? url += '&size=' + size : url;
            url = sort ? url += '&sort=' + sort : url;
            return this.backendService
                .get(url)
                .then((dataUsagesHal: ng.IHttpPromiseCallbackArg<IHalList<ISIMDataUsages>>) => {
                    let dataUsages: IPaginatedList<ISIMDataUsages>;
                    dataUsages = HalConverter.convertPaginatedList(dataUsagesHal.data, Models.SIMDataUsages.Empty);
                    if (dataUsages.elements.length > 0) {
                        let firstDate: Date = new Date(dataUsages.elements[dataUsages.elements.length - 1].date as string);
                        firstDate.setHours(firstDate.getUTCHours());
                        firstDate.setSeconds(firstDate.getSeconds() + 1);
                        let firstDateStr: string = Common.Utils.dateToString(firstDate);
                        let firstDateDataRemainingUrl: string = this.dataUsageUrl + 'dataRemaining/' + simId + '/inDate/' + firstDateStr;
                        return this.backendService
                            .get(firstDateDataRemainingUrl)
                            .then((remainingData: ng.IHttpPromiseCallbackArg<number>) => {
                                dataUsages.elements[dataUsages.elements.length - 1].balance = remainingData.data;
                                for (let i: number = dataUsages.elements.length - 2; i >= 0; i--) {
                                    if (dataUsages.elements[i].operationType === 'INI')
                                        dataUsages.elements[i].balance = dataUsages.elements[i].operationValue;
                                    else
                                        dataUsages.elements[i].balance = dataUsages.elements[i + 1].balance +
                                            dataUsages.elements[i].signedOperationValue;
                                }
                                return dataUsages;
                            });
                    } else {
                        return dataUsages;
                    }
                 });
        }

        public getRemainingDataChart(simId: number, cFrom: Date, cTo: Date, res: number): ng.IPromise<Models.ISIMDataRemainingChart> {
            return this.backendService
                .get(this.dataRemainingChartUrl + simId + this.graphSearchString(cFrom, cTo, res))
                .then((result: ng.IHttpPromiseCallbackArg<Models.ISIMDataRemainingChart>) => {
                    let chartData: Models.ISIMDataRemainingChart = result.data;
                    let chartDataValues: Models.ISIMDataRemainingChartPoint[] = chartData.values;
                    chartDataValues = this.sortValuesByDate(chartDataValues);
                    chartData.values = chartDataValues;
                    chartData.maxValue = 0;
                    chartData.minValue = 100000000000000000000000000;
                    chartData.values.forEach((chartPoint: Models.ISIMDataRemainingChartPoint) => {
                        if (chartPoint.value > chartData.maxValue) chartData.maxValue = chartPoint.value;
                        if (chartPoint.value < chartData.minValue) chartData.minValue = chartPoint.value;
                        chartPoint.xDate = new Date(chartPoint.timestamp);
                    });
                    return chartData;
                });
        }

        public getDataUsageChart(simId: number, cFrom: Date, cTo: Date, res: number): ng.IPromise<Models.ISIMDataUsageChart> {
            return this.backendService
                .get(this.dataUsageChartUrl + simId + this.graphSearchString(cFrom, cTo, res))
                .then((result: ng.IHttpPromiseCallbackArg<Models.ISIMDataRemainingChart>) => {
                    let chartData: Models.ISIMDataRemainingChart = result.data;
                    chartData.values = this.sortValuesByDate(chartData.values);
                    chartData.maxValue = 0;
                    chartData.minValue = 100000000000000000000000000;
                    chartData.values.forEach((chartPoint: Models.ISIMDataRemainingChartPoint) => {
                        if (chartPoint.value > chartData.maxValue) chartData.maxValue = chartPoint.value;
                        if (chartPoint.value < chartData.minValue) chartData.minValue = chartPoint.value;
                        chartPoint.xDate = new Date(chartPoint.timestamp);
                    });
                    return chartData;
                });
        }

        public getNetworkNode(simId: number): ng.IPromise<Models.INetworkNodeModemAdapter> {
            return this.backendService
                .get(this.backendUrl + '/networkNodeModems/')
                .then((modem: ng.IHttpPromiseCallbackArg<Models.INetworkNodeModem>) => {
                    return this.backendService
                        .get(this.networkNodeUrl + '/' + Common.Utils.idFromHref(modem.data.href))
                        .then((node: ng.IHttpPromiseCallbackArg<Models.NetworkNode>) => {
                            let modemAdapter: Models.NetworkNodeModemAdapter;
                            modemAdapter.href = modem.data.href;
                            modemAdapter.imei = modem.data.imei;
                            modemAdapter.networkNode = node.data;
                            return modemAdapter;
                        });
                });
        }

        public setRemainingData(simHref: string, dataAmount: number, date: Date): ng.IPromise<number> {
            return this.backendService
                .post(this.dataUsageUrl, {
                    sim : simHref,
                    date: date,
                    operationType: "INI",
                    operationValue: dataAmount,
                    receivedValue: 0,
                });
        }

        public addData(simHref: string, dataAmount: number, date: Date): ng.IPromise<number> {
            return this.backendService
                .post(this.dataUsageUrl, {
                    sim : simHref,
                    date: date,
                    operationType: "ADD",
                    operationValue: dataAmount,
                    receivedValue: 0,
                });
        }

        public deleteSIM(sim: Application.Models.ISIM): ng.IPromise<boolean> {
            return this.backendService.delete(sim.href);
        }

        public getAllBySimPlanId(simPlanId: number): ng.IPromise<ISIM[]> {
            return this.backendService
                .get(this.backendUrl + "/search/findAllBySimPlanId?simPlanId=" + simPlanId)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<ISIM>>) => {
                    let halElement: IHalList<ISIM> = result.data;
                    return HalConverter.convertList(halElement, SIM.Empty);
                });
        } 

        public getSIMByIccidPromise(iccid: string, showErrMssgs: boolean = true): ng.IPromise<ISIM> {
            return this.backendService
                .get(this.backendUrl + "/search/findByIccid?iccid=" + iccid, false, undefined, false, showErrMssgs)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<ISIM>>) => {
                    let halElement: IHalElement<SIM> = result.data;
                    let converted: SIM = HalConverter.convertElement(halElement, SIM.Empty);
                    return converted;
                })
            ;
        }

        public getDashboardData(): ng.IPromise<Models.ISIMDashboardData> {
            return this.backendService
                .get(this.dashboardDataUrl)
                .then((result: ng.IHttpPromiseCallbackArg<Models.ISIMDashboardData>) => {
                    return result.data;
                });
        }

        public getDashboardMobileDevices(): ng.IPromise<Models.ISIMDashboardData> {
            return this.backendService
                .get(this.dashboardMobileDevicesUrl)
                .then((result: ng.IHttpPromiseCallbackArg<Models.ISIMDashboardData>) => {
                    return result.data;
                });
        }

        // sims
        private getAllPaginatedSims(page?: number, size?: number, sort?: string): ng.IPromise<Models.IPaginatedList<SIM>> {
            let url: string = this.backendUrl + '?';
            url = page ? url += '&page=' + page : url;
            url = size ? url += '&size=' + size : url;
            url = sort ? url += '&sort=' + sort : url;
            return this.backendService
                .get(url, true)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<SIM>>) => {
                    let convertedList: Models.IPaginatedList<SIM>;
                    convertedList = HalConverter.convertPaginatedList(result.data, SIM.Empty);
                    return convertedList;
                });
        }

        // sims
        private getAllSimPlans(): ng.IPromise<ISimPlan[]> {
            return this.simPlanService
                .getAllSimPlansPromise()
                .then((simPlans: Models.SimPlan[]) => {
                    return simPlans;
                });
        }

        private getSIMByIdPromise(id: number): ng.IPromise<Models.SIM> {
            return this.backendService
                .get(this.backendUrl + "/" + id)
                .then((data: IHalElement<SIM>) => {
                    let halElement: IHalElement<SIM> = data;
                    let converted: ng.IHttpPromiseCallbackArg<SIM> = HalConverter.convertElement(halElement, SIM.Empty);
                    converted.data.href = this.backendUrl + "/" + id;
                    return converted.data;
                });
        }

        private getSIMPlanBySIMIdPromise(id: number): ng.IPromise<Models.SimPlan> {
            return this.simPlanService
                .getSimPlanPromise(this.backendBaseUrl + '/sims/' + id + '/simPlan')
                .then((simPlan: Models.SimPlan) => {
                    return simPlan;
                });
        }

        private sortValuesByDate(values: Models.ISIMDataRemainingChartPoint[]): Models.ISIMDataRemainingChartPoint[] {
            values.sort(function(a: Models.ISIMDataRemainingChartPoint, b: Models.ISIMDataRemainingChartPoint): number {
                if (a.x > b.x)
                    return 1;
                else if (a.x < b.x)
                    return -1;
                else
                    return 0;
            });
            return values;
        }

        private graphSearchString(cFrom: Date, cTo: Date, res: number): string {
            let searchString: string = '';
            if (cFrom)
                searchString += '?from=' + Common.Utils.dateToString(cFrom);
            if (cTo) {
                let now: Date = new Date();
                if (cTo > now) cTo = now;
                (!cFrom) ? searchString += '?' : searchString += '&';
                searchString += 'to=' + Common.Utils.dateToString(cTo);
            }
            if (res) {
                (!cFrom && !cTo) ? searchString += '?' : searchString += '&';
                searchString += 'res=' + res;
            }
            return searchString;
        }

        private burnishSIM(sim: ISIM): ISIM {
            this.getRemainingData(Common.Utils.idFromHref(sim.href))
                .then((value: number) => {
                    sim.remainingData = value;
                });
            this.loadSimPlan(sim);
            return sim;
        }

        /**
         * Load the full SimPlan element and set it into sim object.
         */
        private loadSimPlan(sim: SIM): void {
            this.simPlanService
                .findByHref(sim.simPlan.href, (simPlan: ISimPlan) => {
                    sim.simPlan = simPlan;
                    if (sim.networkNodeModem)
                        this.networkNodeModemService.findByHref(sim.networkNodeModem.href, (modem: Models.INetworkNodeModem) => {
                            sim.networkNodeModem.href = modem.href;
                            sim.networkNodeModem.imei = modem.imei;
                            sim.networkNodeModem.slot = modem.slot;
                            this.networkNodeService.getById(modem.networkNode, (networkNode: INetworkNode) => {
                                sim.networkNodeModem.networkNode = networkNode;
                            });
                        });
                });
        }
    }
}
